/**
 * AuthorizationGate Component
 * 
 * A component that gates content based on policy-based authorization.
 * Combines capability checks with policy evaluation.
 * 
 * Usage:
 * 
 * Basic:
 *   <AuthorizationGate action="items.edit" resource={resource}>
 *     <EditButton onClick={handleEdit} />
 *   </AuthorizationGate>
 * 
 * With fallback:
 *   <AuthorizationGate 
 *     action="items.delete" 
 *     resource={resource}
 *     fallback={<DisabledButton reason="Cannot delete" />}
 *   >
 *     <DeleteButton onClick={handleDelete} />
 *   </AuthorizationGate>
 * 
 * With reason display:
 *   <AuthorizationGate 
 *     action="lists.publish" 
 *     resource={resource}
 *     showReason
 *   >
 *     <PublishButton />
 *   </AuthorizationGate>
 */

import React, { type ReactNode } from 'react';
import { useAuthorization } from '../../hooks/prioritization/useAuthorization';
import { DisabledReason } from './DisabledReason';
import type { ResourceContext, EvaluationContext } from '../../lib/authorization/types';

// ============================================================================
// Props
// ============================================================================

export interface AuthorizationGateProps {
  /** Action to check authorization for */
  action: string;
  
  /** Resource being acted upon */
  resource: ResourceContext | null;
  
  /** Optional evaluation context overrides */
  context?: Partial<EvaluationContext>;
  
  /** Children to render if authorized */
  children: ReactNode;
  
  /** Fallback to render if not authorized (defaults to null) */
  fallback?: ReactNode;
  
  /** Show reason when not authorized */
  showReason?: boolean;
  
  /** Style for reason display */
  reasonStyle?: 'inline' | 'tooltip' | 'block';
  
  /** Loading content while checking authorization */
  loading?: ReactNode;
  
  /** Whether to disable children instead of hiding (wraps in span with disabled state) */
  disableInsteadOfHide?: boolean;
  
  /** Callback when authorization is denied */
  onDenied?: (reason: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function AuthorizationGate({
  action,
  resource,
  context,
  children,
  fallback = null,
  showReason = false,
  reasonStyle = 'inline',
  loading = null,
  disableInsteadOfHide = false,
  onDenied,
}: AuthorizationGateProps): React.ReactElement | null {
  const { allowed, loading: isLoading, decision, explain } = useAuthorization(
    action,
    resource,
    context
  );
  
  // Handle loading state
  if (isLoading) {
    if (loading) {
      return <>{loading}</>;
    }
    // Default: render nothing while loading
    return null;
  }
  
  // Handle authorized case
  if (allowed) {
    return <>{children}</>;
  }
  
  // Handle denied case
  if (onDenied && explain) {
    onDenied(explain.summary);
  }
  
  // Disable instead of hide
  if (disableInsteadOfHide) {
    return (
      <DisabledWrapper showReason={showReason} decision={decision} style={reasonStyle}>
        {children}
      </DisabledWrapper>
    );
  }
  
  // Show reason
  if (showReason && decision) {
    return (
      <DisabledReason 
        decision={decision} 
        style={reasonStyle === 'block' ? 'block' : 'inline'} 
      />
    );
  }
  
  // Return fallback or null
  return <>{fallback}</>;
}

// ============================================================================
// Disabled Wrapper
// ============================================================================

interface DisabledWrapperProps {
  children: ReactNode;
  showReason: boolean;
  decision: AuthorizationGateProps['resource'] extends null ? null : ReturnType<typeof useAuthorization>['decision'];
  style: 'inline' | 'tooltip' | 'block';
}

function DisabledWrapper({ 
  children, 
  showReason, 
  decision,
  style,
}: DisabledWrapperProps): React.ReactElement {
  const content = (
    <span 
      className="opacity-50 cursor-not-allowed pointer-events-none"
      aria-disabled="true"
    >
      {children}
    </span>
  );
  
  if (showReason && decision && style === 'tooltip') {
    const reason = decision.reasons[0];
    return (
      <span 
        className="relative group"
        title={reason?.code || 'Not authorized'}
      >
        {content}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {reason?.code || 'Not authorized'}
        </span>
      </span>
    );
  }
  
  if (showReason && decision && style === 'inline') {
    return (
      <span className="inline-flex items-center gap-2">
        {content}
        <DisabledReason decision={decision} style="minimal" />
      </span>
    );
  }
  
  return content;
}

// ============================================================================
// Render Prop Variant
// ============================================================================

export interface AuthorizationGateRenderProps {
  allowed: boolean;
  loading: boolean;
  decision: ReturnType<typeof useAuthorization>['decision'];
  explain: ReturnType<typeof useAuthorization>['explain'];
}

export interface AuthorizationGateWithRenderProps {
  action: string;
  resource: ResourceContext | null;
  context?: Partial<EvaluationContext>;
  children: (props: AuthorizationGateRenderProps) => ReactNode;
}

/**
 * Render prop variant for more control over rendering.
 * 
 * Usage:
 *   <AuthorizationGateRender action="items.edit" resource={resource}>
 *     {({ allowed, explain }) => (
 *       <Button disabled={!allowed} title={explain?.summary}>
 *         Edit
 *       </Button>
 *     )}
 *   </AuthorizationGateRender>
 */
export function AuthorizationGateRender({
  action,
  resource,
  context,
  children,
}: AuthorizationGateWithRenderProps): React.ReactElement {
  const result = useAuthorization(action, resource, context);
  
  return <>{children(result)}</>;
}

// ============================================================================
// HOC Variant
// ============================================================================

/**
 * Higher-order component for authorization.
 * 
 * Usage:
 *   const ProtectedEditButton = withAuthorization(EditButton, 'items.edit');
 */
export function withAuthorization<P extends object>(
  Component: React.ComponentType<P>,
  action: string,
  options?: {
    fallback?: ReactNode;
    showReason?: boolean;
    getResource?: (props: P) => ResourceContext | null;
  }
) {
  const { fallback, showReason, getResource } = options || {};
  
  return function AuthorizedComponent(
    props: P & { resource?: ResourceContext | null }
  ): React.ReactElement | null {
    const resource = getResource ? getResource(props) : props.resource || null;
    
    return (
      <AuthorizationGate
        action={action}
        resource={resource}
        fallback={fallback}
        showReason={showReason}
      >
        <Component {...props} />
      </AuthorizationGate>
    );
  };
}

// ============================================================================
// Multiple Actions Gate
// ============================================================================

export interface MultiActionGateProps {
  /** Actions to check (all must be allowed) */
  actions: Array<{
    action: string;
    resource: ResourceContext | null;
    context?: Partial<EvaluationContext>;
  }>;
  
  /** How to combine results: 'all' (default) or 'any' */
  mode?: 'all' | 'any';
  
  /** Children to render if authorized */
  children: ReactNode;
  
  /** Fallback to render if not authorized */
  fallback?: ReactNode;
}

/**
 * Gate that checks multiple actions.
 * 
 * Usage:
 *   <MultiActionGate 
 *     actions={[
 *       { action: 'items.edit', resource: itemResource },
 *       { action: 'lists.edit', resource: listResource },
 *     ]}
 *     mode="all"
 *   >
 *     <BulkEditButton />
 *   </MultiActionGate>
 */
export function MultiActionGate({
  actions,
  mode = 'all',
  children,
  fallback = null,
}: MultiActionGateProps): React.ReactElement | null {
  // Check each action
  const results = actions.map(({ action, resource, context }) => 
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAuthorization(action, resource, context)
  );
  
  const isLoading = results.some(r => r.loading);
  if (isLoading) return null;
  
  const isAuthorized = mode === 'all'
    ? results.every(r => r.allowed)
    : results.some(r => r.allowed);
  
  if (isAuthorized) {
    return <>{children}</>;
  }
  
  return <>{fallback}</>;
}

export default AuthorizationGate;
